import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Form,
    Input,
    InputNumber,
    Switch,
    Button,
    Card,
    Typography,
    Row,
    Col,
    Spin,
    Space,
    Divider,
    Alert,
    Select,
    Tabs
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, CalculatorOutlined, SettingOutlined, FilePdfOutlined, PrinterOutlined, LinkOutlined } from '@ant-design/icons';
import { EligibilitySimulator } from '../components/EligibilitySimulator';
import {
    useLoanEligibilityConfigById,
    useCreateLoanEligibilityConfig,
    useUpdateLoanEligibilityConfig
} from '../hooks/useLoanEligibilityConfig';
import { LoanEligibilityConfigDTO } from '@/shared/api/model';
import { useTheme } from "@/components/common/ThemeProvider";
import { useGetAllActiveProductProperties } from '@/shared/api/client/product-properties-resource';
import SelfRegistrationDeductionForm from '@/modules/invitations/components/SelfRegistrationDeductionForm';
// import { HAZINA_DEDUCTION_FORM_CONFIG, DeductionFormConfig } from '@/modules/loans/components/DeductionFormConfig'; // TODO: Component not implemented yet
import { useCompany } from '@/hooks/company/use-company';
import { useGetActiveConfig } from '@/modules/onboarding/hooks/useSelfOnboardingConfig';
import { useReactToPrint } from 'react-to-print';
import { useRef } from 'react';

// Stub for missing loans module constant
const HAZINA_DEDUCTION_FORM_CONFIG: any = {};

const { Title, Text } = Typography;
const { TextArea } = Input;

const LoanEligibilityConfigFormView: React.FC = () => {
    const navigate = useNavigate();
    const { dark } = useTheme();
    const { id } = useParams<{ id: string }>();
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState('config');
    const [liveConfig, setLiveConfig] = useState<Partial<LoanEligibilityConfigDTO>>({});
    const printableRef = useRef<HTMLDivElement>(null);

    const isEditMode = id && id !== 'new';
    const configId = isEditMode ? parseInt(id) : undefined;

    const { data: config, isLoading } = useLoanEligibilityConfigById(configId);
    const createConfig = useCreateLoanEligibilityConfig();
    const updateConfig = useUpdateLoanEligibilityConfig();
    
    const { data: companyData } = useCompany();
    const { data: activeOnboardingConfig } = useGetActiveConfig();

    const handlePrint = useReactToPrint({
        content: () => printableRef.current,
        documentTitle: `Deduction_Form_Preview_${Date.now()}`,
        pageStyle: `@page { size: A4; margin: 10mm; } @media print { body { -webkit-print-color-adjust: exact; } }`,
    });

    const { data: productsResponse, isLoading: isLoadingProducts } = useGetAllActiveProductProperties({
        query: { enabled: true }
    });
    
    const loanProducts = productsResponse || [];

    const activeDeductionFormConfig: any = (() => {
        const raw = (activeOnboardingConfig as any)?.deductionFormConfig;
        if (raw) { try { return JSON.parse(raw); } catch {} }
        return HAZINA_DEDUCTION_FORM_CONFIG;
    })();

    // Load form data when editing
    useEffect(() => {
        if (config && isEditMode) {
            const glNosArray = config.considerLoanGlNos
                ? config.considerLoanGlNos.split(',').map(s => s.trim()).filter(Boolean)
                : [];
            const configValues = {
                ...config,
                // Ensure boolean values are properly set
                considerExistingLoans: config.considerExistingLoans ?? true,
                active: config.active ?? true,
                enforceProductLimits: config.enforceProductLimits ?? true,
                productSpecificBalanceOnly: config.productSpecificBalanceOnly ?? false,
                applyOneThirdRule: config.applyOneThirdRule ?? false,
                oneThirdRuleMultiplier: config.oneThirdRuleMultiplier ?? 0.33,
                // Convert comma-separated string to array for multi-select
                considerLoanGlNos: glNosArray
            };
            form.setFieldsValue(configValues);
            // For liveConfig (simulator), convert back to string
            setLiveConfig({
                ...configValues,
                considerLoanGlNos: glNosArray.length > 0 ? glNosArray.join(',') : undefined
            });
        }
    }, [config, isEditMode, form]);

    // Update live config when form values change (for simulator)
    const handleFormValuesChange = (_: any, allValues: any) => {
        const liveConfigValues = {
            ...allValues,
            // Convert array to string for liveConfig
            considerLoanGlNos: Array.isArray(allValues.considerLoanGlNos) && allValues.considerLoanGlNos.length > 0
                ? allValues.considerLoanGlNos.join(',')
                : undefined
        };
        setLiveConfig(liveConfigValues);
    };

    // Handle form submission
    const handleSubmit = async (values: any) => {
        const data: LoanEligibilityConfigDTO = {
            ...values,
            id: configId,
            // Convert array back to comma-separated string for storage
            considerLoanGlNos: Array.isArray(values.considerLoanGlNos) && values.considerLoanGlNos.length > 0
                ? values.considerLoanGlNos.join(',')
                : null
        };

        if (isEditMode && configId) {
            updateConfig.mutate(
                { id: configId, data },
                {
                    onSuccess: () => {
                        navigate('/admin/loan-settings/eligibility');
                    }
                }
            );
        } else {
            createConfig.mutate(data, {
                onSuccess: () => {
                    navigate('/admin/loan-settings/eligibility');
                }
            });
        }
    };

    if (isLoading && isEditMode) {
        return (
            <Card>
                <Spin size="large" tip="Loading configuration..." />
            </Card>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Card
                style={{
                    backgroundColor: dark ? '#1f1f1f' : '#ffffff',
                    borderRadius: '8px'
                }}
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                    <Col>
                        <Space>
                            <Button
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate('/admin/loan-settings/eligibility')}
                            >
                                Back
                            </Button>
                            <Title level={3} style={{ margin: 0 }}>
                                {isEditMode ? 'Edit Configuration' : 'New Configuration'}
                            </Title>
                        </Space>
                    </Col>
                </Row>

                <Alert
                    message="Configuration Priority"
                    description="When multiple configurations match (e.g., branch-specific and global), the one with higher priority wins. Branch+Product specific configs have highest precedence, followed by Branch-only, Product-only, and Global configs."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'config',
                            label: (
                                <span>
                                    <SettingOutlined />
                                    Configuration
                                </span>
                            ),
                            children: null
                        },
                        {
                            key: 'simulator',
                            label: (
                                <span>
                                    <CalculatorOutlined />
                                    Simulator
                                </span>
                            ),
                            children: null
                        },
                        {
                            key: 'preview-form',
                            label: (
                                <span>
                                    <FilePdfOutlined />
                                    Preview Form
                                </span>
                            ),
                            children: null
                        }
                    ]}
                    style={{ marginBottom: 16 }}
                />

                {activeTab === 'simulator' && (
                    <EligibilitySimulator config={liveConfig} />
                )}

                {activeTab === 'preview-form' && (
                    <div>
                        <Alert
                            message="Deduction Form Preview"
                            description={
                                <span>
                                    This is a read-only preview of the deduction form using the <strong>active self-registration configuration</strong>.
                                    To change the logo, stamp, or watermark go to <strong>Self-Registration Config</strong> and edit the active configuration.
                                </span>
                            }
                            type="info"
                            showIcon
                            action={
                                <Button
                                    size="small"
                                    icon={<LinkOutlined />}
                                    onClick={() => navigate('/admin/self-registration/config')}
                                >
                                    Go to Self-Registration Config
                                </Button>
                            }
                            style={{ marginBottom: 16 }}
                        />
                        <Space style={{ marginBottom: 16 }}>
                            <Button
                                type="primary"
                                icon={<PrinterOutlined />}
                                onClick={() => handlePrint()}
                                disabled={!companyData}
                            >
                                Print Preview
                            </Button>
                            {!(activeOnboardingConfig as any)?.deductionFormConfig && (
                                <Alert
                                    message="No deduction form config set — showing Hazina defaults."
                                    type="warning"
                                    showIcon
                                    style={{ margin: 0 }}
                                />
                            )}
                        </Space>
                        <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
                            <div ref={printableRef}>
                                <SelfRegistrationDeductionForm
                                    session={{
                                        fullName: 'JOHN MWANACHAMA',
                                        sessionToken: 'PREVIEW12345',
                                        employerName: 'OFISI YA RAIS',
                                        voteNumber: '123456',
                                        employeeId: 'EMP-001',
                                        monthlyDeductionAmount: 150000,
                                        officerApprovedBy: 'DISBURSEMENT OFFICER',
                                    } as any}
                                    company={companyData || {}}
                                    deductionFormConfig={activeDeductionFormConfig}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ display: activeTab === 'config' ? 'block' : 'none' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={handleFormValuesChange}
                    initialValues={{
                        considerExistingLoans: true,
                        active: true,
                        priority: 0,
                        defaultSavingsMultiplier: 3,
                        highBalanceMultiplier: 2,
                        enforceProductLimits: true,
                        productSpecificBalanceOnly: false,
                        applyOneThirdRule: false,
                        oneThirdRuleMultiplier: 0.33
                    }}
                >
                    <Divider orientation="left">Basic Information</Divider>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="configName"
                                label="Configuration Name"
                                rules={[
                                    { required: true, message: 'Please enter configuration name' }
                                ]}
                            >
                                <Input placeholder="e.g., Default Config, Branch A Config" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="branch" label="Branch Code">
                                <Input placeholder="Leave empty for all branches" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item name="glNo" label="GL Number">
                                <Input placeholder="Leave empty for all products" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item 
                                name="defaultLoanGlNo" 
                                label="Default Loan Product"
                                tooltip="The loan product to use when no product is specified in eligibility checks"
                            >
                                <Select
                                    placeholder="Select default loan product"
                                    allowClear
                                    showSearch
                                    loading={isLoadingProducts}
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={loanProducts.map(product => ({
                                        value: product.glNo,
                                        label: `${product.productName} (${product.glNo})`,
                                        product: product
                                    }))}
                                    optionRender={(option) => (
                                        <Space direction="vertical" size={0}>
                                            <Text strong>{option.data.product?.productName}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                GL: {option.data.product?.glNo}
                                                {option.data.product?.maximumAmount &&
                                                    ` • Max: ${option.data.product.maximumAmount.toLocaleString()}`}
                                            </Text>
                                        </Space>
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="priority" label="Priority">
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder="Higher priority wins"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="active"
                                label="Status"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Default Tier Configuration</Divider>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="defaultMaxAmount"
                                label="Default Maximum Amount"
                                rules={[
                                    { required: true, message: 'Please enter default max amount' }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) =>
                                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                    }
                                    // @ts-ignore
                                    parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                                    placeholder="e.g., 30000000"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="defaultSavingsMultiplier"
                                label="Default Savings Multiplier"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter default multiplier'
                                    }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    step={0.1}
                                    style={{ width: '100%' }}
                                    placeholder="e.g., 3 (for 3x savings)"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">High Balance Tier Configuration</Divider>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="highBalanceThreshold"
                                label="High Balance Threshold"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter high balance threshold'
                                    }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) =>
                                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                    }
                                    // @ts-ignore
                                    parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                                    placeholder="e.g., 45000000"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="highBalanceMaxAmount"
                                label="High Balance Maximum Amount"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter high balance max amount'
                                    }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) =>
                                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                    }
                                    // @ts-ignore
                                    parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                                    placeholder="e.g., 50000000"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="highBalanceMultiplier"
                                label="High Balance Multiplier"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please enter high balance multiplier'
                                    }
                                ]}
                            >
                                <InputNumber
                                    min={0}
                                    step={0.1}
                                    style={{ width: '100%' }}
                                    placeholder="e.g., 2 (for 2x savings)"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Additional Constraints (Optional)</Divider>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="minimumSavingsBalance"
                                label="Minimum Savings Balance"
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    formatter={(value) =>
                                        `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                    }
                                    // @ts-ignore
                                    parser={(value) => Number(value!.replace(/\$\s?|(,*)/g, ''))}
                                    placeholder="Leave empty for no minimum"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="maxLoanToSavingsRatio"
                                label="Max Loan-to-Savings Ratio"
                            >
                                <InputNumber
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    style={{ width: '100%' }}
                                    placeholder="e.g., 0.8 for 80%"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="minSavingsHistoryMonths"
                                label="Minimum Savings History (Months)"
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder="Leave empty for no requirement"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="considerExistingLoans"
                                label="Consider Existing Loans"
                                valuePropName="checked"
                                tooltip="When enabled, existing loan balances will be deducted from eligibility"
                            >
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Product & Income Rules</Divider>

                    <Alert
                        message="Product Limits & 1/3 Rule"
                        description="Configure how product-specific limits and income-based rules affect eligibility. The 1/3 rule ensures loan repayments don't exceed a portion of customer's income/savings."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />

                    <Row gutter={16}>
                        <Col xs={24}>
                            <Form.Item
                                name="considerLoanGlNos"
                                label="Loan Products Affecting Eligibility"
                                tooltip="Select the loan products whose outstanding balances reduce eligibility. If none are selected, all outstanding loan balances are considered."
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Leave empty to consider all loans"
                                    allowClear
                                    showSearch
                                    loading={isLoadingProducts}
                                    optionFilterProp="label"
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={loanProducts.map(product => ({
                                        value: product.glNo,
                                        label: `${product.productName} (${product.glNo})`
                                    }))}
                                    notFoundContent={isLoadingProducts ? 'Loading...' : 'No products found'}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="enforceProductLimits"
                                label="Enforce Product Limits"
                                valuePropName="checked"
                                tooltip="When enabled, eligibility is capped by the product's maximum amount minus outstanding balance for that product"
                            >
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="productSpecificBalanceOnly"
                                label="Product-Specific Balance Only"
                                valuePropName="checked"
                                tooltip="When enabled, only consider the outstanding balance for this specific product. When disabled, consider all outstanding loans."
                            >
                                <Switch
                                    checkedChildren="This Product Only"
                                    unCheckedChildren="All Loans"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="applyOneThirdRule"
                                label="Apply 1/3 Rule"
                                valuePropName="checked"
                                tooltip="When enabled, loan eligibility is limited based on the 1/3 income rule (loan repayment should not exceed 1/3 of income)"
                            >
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="oneThirdRuleMultiplier"
                                label="1/3 Rule Multiplier"
                                tooltip="The multiplier for the 1/3 rule (default 0.33 for 1/3, can be adjusted e.g., 0.40 for 40%)"
                            >
                                <InputNumber
                                    min={0}
                                    max={1}
                                    step={0.01}
                                    style={{ width: '100%' }}
                                    placeholder="e.g., 0.33 for 1/3"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Notes</Divider>

                    <Form.Item name="notes" label="Additional Notes">
                        <TextArea
                            rows={4}
                            placeholder="Enter any additional notes or description for this configuration"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={createConfig.isPending || updateConfig.isPending}
                                size="large"
                            >
                                {isEditMode ? 'Update Configuration' : 'Create Configuration'}
                            </Button>
                            <Button
                                onClick={() => navigate('/admin/loan-settings/eligibility')}
                                size="large"
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
                </div>
            </Card>
        </div>
    );
};

export default LoanEligibilityConfigFormView;
